import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    forwardRef,
    input,
    signal
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormsModule,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator
} from '@angular/forms';
import { CommonModule } from '@angular/common';


export interface PasswordPolicy {
    minLength: number;
    requireDigits: boolean;
    requireUppercase: boolean;
    requireNonAlphanumeric: boolean;
}

export interface PasswordValidationResult {
    isValid: boolean;
    minLengthMet: boolean;
    digitsMet: boolean;
    uppercaseMet: boolean;
    nonAlphanumericMet: boolean;
}

@Component({
    selector: 'app-password-input',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './password-input.html',
    styleUrl: './password-input.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PasswordInput),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => PasswordInput),
            multi: true
        }
    ]
})
export class PasswordInput implements ControlValueAccessor, Validator {
    readonly placeholder = input('Ingrese la contraseña');
    readonly ariaLabel = input('Contraseña');
    readonly showPolicy = input(true);
    readonly showPassword = signal(false);
    readonly required = input(false);

    readonly policy = input<PasswordPolicy>({
        minLength: 8,
        requireDigits: true,
        requireUppercase: true,
        requireNonAlphanumeric: true
    });

    readonly passwordValue = signal('');
    readonly isTouched = signal(false);
    readonly isDisabled = signal(false);

    readonly validation = computed(() => this.evaluatePassword(this.passwordValue()));

    private onChange: (value: string) => void = () => { };
    private onTouched: () => void = () => { };
    private onValidatorChange: (() => void) | null = null;

    constructor() {
        effect(() => {
            this.required();
            this.policy();
            this.onValidatorChange?.();
        });
    }

    writeValue(value: string | null): void {
        this.passwordValue.set(value ?? '');
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    registerOnValidatorChange(fn: () => void): void {
        this.onValidatorChange = fn;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        const value = typeof control.value === 'string' ? control.value : '';
        const validation = this.evaluatePassword(value);

        if (!value) {
            return this.required() ? { required: true } : null;
        }

        if (validation.isValid) {
            return null;
        }

        return {
            passwordPolicy: {
                ...validation,
                required: this.required()
            }
        };
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled.set(isDisabled);
    }

    onPasswordChange(event: string): void {
        const value = event;
        this.passwordValue.set(value);
        this.onChange(value);
    }

    onPasswordBlur(): void {
        this.isTouched.set(true);
        this.onTouched();
    }

    private evaluatePassword(password: string): PasswordValidationResult {
        if (!password) {
            return {
                isValid: !this.required(),
                minLengthMet: !this.required(),
                digitsMet: !this.required(),
                uppercaseMet: !this.required(),
                nonAlphanumericMet: !this.required()
            };
        }

        const pol = this.policy();

        const minLengthMet = password.length >= pol.minLength;
        const digitsMet = !pol.requireDigits || /\d/.test(password);
        const uppercaseMet = !pol.requireUppercase || /[A-Z]/.test(password);
        const nonAlphanumericMet = !pol.requireNonAlphanumeric || /[^a-zA-Z0-9]/.test(password);

        return {
            isValid: minLengthMet && digitsMet && uppercaseMet && nonAlphanumericMet,
            minLengthMet,
            digitsMet,
            uppercaseMet,
            nonAlphanumericMet
        };
    }
}
